import React, { useMemo } from 'react'
import moment from 'moment'
import injectStyles from 'react-jss'
import { StatusLabel } from '../../../../../Components/StatusLabel'
import {
  dropTimeZoneFromDate,
  browserSafeNewDate,
} from '../../../../../shared/libraries/utils/date'
import { DATE_FORMAT } from '../../../../../constants'
import { getPrimaryAndSecondaryMetricResults } from '../../../../../Utils/Mission'
import { dateSort, timeOfDaySort, statusSort } from '../../../../../Utils/Table'
import SimpleTableTable from '../../../../../Components/SimpleTableTable'
import { MissionStarRatingWithHint } from 'Components/MissionStarRatingWithHint'
import {
  MISSION_ENUM_TYPES,
  MISSION_STATUS_ENUM,
} from '../../../../../shared/constants'
import TooltipV2 from '../../../../../Components/TooltipV2/TooltipV2'
import { getLocationProfileLink } from '../../../../../Utils/Link'
import { authorization } from '../../../../../Module/Auth/Auth'

const styles = {
  wrapper: {
    '& .SearchAndFilters': {
      paddingLeft: '40px',
    },
    '& table': {
      marginLeft: '-25px',
    },
    '& .Pagination': {
      marginLeft: '40px',
    },
  },
}

const getConversionsInfo = ({ conversions }) => {
  return conversions.reduce(
    (acc, c) => {
      if (!c.metric.isPrimary) return acc

      if (c.bonusDisqualified) {
        acc.disqualifiedConversions += 1
        acc.totalConversions += 1
        return acc
      }
      if (c.paid) {
        acc.paidConversions += 1
      }

      if (!c.paid && c.inRedemption) {
        acc.pendingConversions += 1
      }
      acc.totalConversions += 1
      return acc
    },
    {
      totalConversions: 0,
      paidConversions: 0,
      pendingConversions: 0,
      disqualifiedConversions: 0,
    },
  )
}

const ambassadorActivityMapper = ambassadorActivity => {
  const {
    id,
    client,
    location,
    timeStart,
    timeOfDay,
    cityPlan,
    isSpied,
    ratingInfo,
    cancelled,
    bonusDisqualified,
    conversions,
    type,
    isBaView,
  } = ambassadorActivity
  const localDate = browserSafeNewDate(dropTimeZoneFromDate(timeStart))
  const metrics = getPrimaryAndSecondaryMetricResults(
    ambassadorActivity.conversions,
    ambassadorActivity.campaignMetrics,
  )

  return {
    id,
    isSpied,
    bonusDisqualified,
    conversions,
    locationId: location ? location.id : null,
    type,
    cityId: cityPlan.city.id,
    isBaView,
    'CLIENT': client.name,
    'DATE': moment(localDate).format(DATE_FORMAT),
    'WEEK': moment(localDate).format('WW'),
    'TIME': timeOfDay,
    'RATING': ratingInfo,
    'CITY': cityPlan.city.name,
    'LOCATION': location ? location.name : '-',
    'METRIC I': metrics.primaryMetric,
    'METRIC II': metrics.secondaryMetric,
    'STATUS': cancelled
      ? MISSION_STATUS_ENUM.CANCELLED
      : ambassadorActivity.status,
    'FLYERS DISTR.': ambassadorActivity.checkout
      ? ambassadorActivity.checkout.flyerQuantity
      : '',
  }
}

const generateDisqualificationMessageContent = conversions => {
  if (!conversions) return
  const conversionsInfo = getConversionsInfo({ conversions })
  return (
    <div>
      <span>This mission has disqualified conversions: </span>
      <br />
      <span>Total Conversions: {conversionsInfo.totalConversions}</span>
      <br />
      <span>Conversions paid: {conversionsInfo.paidConversions}</span>
      <br />
      <span>Conversions to be paid: {conversionsInfo.pendingConversions}</span>
      <br />
      <span>
        Conversions disqualified: {conversionsInfo.disqualifiedConversions}
      </span>
      <br />
    </div>
  )
}

const ambassadorActivitySorter = (a, b) =>
  new Date(b.timeStart).getTime() - new Date(a.timeStart).getTime()

const getActivityTableData = ambassadorActivities =>
  Array.from(ambassadorActivities)
    .sort(ambassadorActivitySorter)
    .map(ambassadorActivityMapper)

const ActivityTable = ({ ambassadorActivities, deleteMissions, ...props }) => {
  const isCityManagerAssistant = authorization.isCityManagerAssistant()
  const header = isCityManagerAssistant
    ? [
        'CLIENT',
        'DATE',
        'WEEK',
        'TIME',
        'RATING',
        'CITY',
        'LOCATION',
        'FLYERS DISTR.',
        'STATUS',
      ]
    : [
        'CLIENT',
        'DATE',
        'WEEK',
        'TIME',
        'RATING',
        'CITY',
        'LOCATION',
        'FLYERS DISTR.',
        'METRIC I',
        'METRIC II',
        'STATUS',
      ]

  const STATUS_COLUMN_INDEX = header.indexOf('STATUS')
  const LOCATION_COLUMN_INDEX = header.indexOf('LOCATION')

  const sortOn = [
    'CLIENT',
    'WEEK',
    'LOCATION',
    'NAME',
    'FLYERS DISTR.',
    'METRIC I',
    'METRIC II',
    { label: 'TIME', sort: timeOfDaySort },
    { label: 'DATE', sort: dateSort },
    { label: 'STATUS', sort: statusSort },
  ]

  const filterOn = [{ label: 'STATUS', sort: statusSort }]

  const data = useMemo(() => getActivityTableData(ambassadorActivities), [
    ambassadorActivities,
  ])

  const postRowRender = ambassadorActivity => {
    const locationLink = getLocationProfileLink(
      ambassadorActivity.cityId,
      ambassadorActivity.locationId,
    )
    return {
      ...ambassadorActivity,
      'LOCATION':
        ambassadorActivity.locationId &&
        ambassadorActivity.type === MISSION_ENUM_TYPES.HANDTOHAND ? (
          <a href={locationLink} target="_blank" rel="noreferrer">
            {ambassadorActivity.LOCATION}
          </a>
        ) : (
          ambassadorActivity.LOCATION
        ),
      'RATING': (
        <div onClick={e => e.stopPropagation()}>
          <MissionStarRatingWithHint
            className="stars"
            id={ambassadorActivity.id}
            ratingInfo={ambassadorActivity.RATING}
            uniqueId={ambassadorActivity.id}
          />
        </div>
      ),
      'STATUS': (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <StatusLabel
            isSpied={ambassadorActivity.isSpied}
            status={ambassadorActivity.STATUS}
          >
            {ambassadorActivity.STATUS}
          </StatusLabel>
          {ambassadorActivity.bonusDisqualified &&
          !isCityManagerAssistant &&
          !ambassadorActivity.isBaView ? (
            <div style={{ marginLeft: 10 }}>
              <TooltipV2
                iconFill={'#F90606'}
                content={generateDisqualificationMessageContent(
                  ambassadorActivity.conversions,
                )}
              />
            </div>
          ) : null}
        </div>
      ),
    }
  }

  const action = ambassadorActivity =>
    window.open(`/dashboard/mission/${ambassadorActivity.id}`, '_blank')

  return (
    <div className={props.classes.wrapper}>
      <SimpleTableTable
        header={header}
        data={data}
        sortOn={sortOn}
        filterOn={filterOn}
        postRowRender={postRowRender}
        action={action}
        showPages={false}
        isHoverRating={true}
        cypress_id={'activitiesTable'}
        disableOpacityColumnsIndexes={[STATUS_COLUMN_INDEX]}
        disableActionColumnIndexes={[LOCATION_COLUMN_INDEX]}
      />
    </div>
  )
}

export default injectStyles(styles)(ActivityTable)
